import { useEffect, useCallback, useMemo } from 'react';
import { useWidgetStore } from '../store';
import QuotaService from '../services/QuotaService';

interface UseQuotaManagementReturn {
  hasQuota: boolean;
  quotaCheckComplete: boolean;
  remainingMinutes: number;
  checkQuota: () => Promise<void>;
}

export const useQuotaManagement = (
  apiUrl: string,
  debug: boolean = false
): UseQuotaManagementReturn => {
  const hasQuota = useWidgetStore((state) => state.hasQuota);
  const quotaCheckComplete = useWidgetStore(
    (state) => state.quotaCheckComplete
  );
  const remainingMinutes = useWidgetStore((state) => state.remainingMinutes);

  const quotaService = useMemo(
    () =>
      new QuotaService(
        {
          apiUrl,
          domain: window.location.hostname,
        },
        debug
      ),
    [apiUrl, debug]
  );

  const checkQuota = useCallback(async () => {
    try {
      const status = await quotaService.checkQuota();
      const hasQuotaResult = status.status !== 'exceeded';
      const remainingMinutesResult = status.status === 'error' ? 0 : status.remainingMinutes;

      useWidgetStore.getState().setHasQuota(hasQuotaResult);
      useWidgetStore.getState().setRemainingMinutes(remainingMinutesResult);
      useWidgetStore.getState().setQuotaCheckComplete(true);
    } catch (error) {
      console.error('[useQuotaManagement] Failed to check quota:', error);
      // On error, assume we have quota
      useWidgetStore.getState().setHasQuota(true);
      useWidgetStore.getState().setQuotaCheckComplete(true);
    }
  }, [quotaService]);

  // Initial quota check
  useEffect(() => {
    checkQuota();
  }, [checkQuota]);

  return {
    hasQuota,
    quotaCheckComplete,
    remainingMinutes,
    checkQuota,
  };
};
